import { useQuery, useQueryClient } from "@tanstack/react-query";
import { INBOX_TOPIC } from "@tcg/realtime";
import { createNotificationClient, createTaxClient } from "@tcg/api-client";
import { Bell, CircleUserRound, LogOut, UserRound, LifeBuoy } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { useAuth } from "@/auth/use-auth";
import { useInboxArrivals } from "@/features/notifications/inboxAlerts";
import { SearchButton } from "@/features/search/SearchProvider";
import { storeLogoUrl, storeName, useStoreSettings } from "@/features/settings/storeSettingsApi";
import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { cn } from "@/lib/utils";
import { useLiveInvalidate, useLiveTopics } from "@/realtime/hooks";
import { LiveStatusMenu } from "@/realtime/LiveStatusMenu";

import { LanguageSelect } from "./language-select";

interface StoreBrand {
  name: string | null;
  logoUrl: string | null;
}

/**
 * Shop name + logo in the header (T11-01) from the shop's details (T01-16, cms-service
 * `GET /v1/admin/settings/store` — every staff member may read it), live on `public:cms`. Before
 * the owner filled in the name, the 事業者名 of the tax settings is used for people with `tax.read`
 * (T11-36); otherwise the header says "Shop admin". Nothing here ever shows an error.
 */
function useStoreBrand(): StoreBrand {
  const { can } = useAuth();
  const { i18n } = useTranslation();
  const tax = useServiceClient(createTaxClient);
  const store = useStoreSettings();
  const fromStore = storeName(store.data, i18n.resolvedLanguage);
  // the tax name is only asked for once the shop's details are known to have no name
  const allowed = can("tax.read") && !store.isPending && fromStore === null;
  const poll = useLiveInvalidate("staff:tax", [STORE_BRAND_KEY], { pollMs: 5 * 60_000 });
  const query = useQuery({
    queryKey: STORE_BRAND_KEY,
    enabled: allowed,
    meta: { silent: true },
    retry: false,
    staleTime: 10 * 60_000,
    refetchInterval: poll,
    queryFn: ({ signal }) => unwrap(tax.GET("/v1/admin/settings/tax", { signal })),
  });
  const ja = query.data?.businessNameJa?.trim() || null;
  const en = (query.data?.businessNameEn ?? query.data?.storeNameEn)?.trim() || null;
  const fromTax = allowed
    ? ((i18n.resolvedLanguage === "ja" ? (ja ?? en) : (en ?? ja)) ?? null)
    : null;
  return { name: fromStore ?? fromTax, logoUrl: storeLogoUrl(store.data) };
}

const STORE_BRAND_KEY = ["shell", "store-brand"] as const;

function InboxBell() {
  const { t } = useTranslation();
  const { can, session } = useAuth();
  const client = useServiceClient(createNotificationClient);
  const allowed = can("inbox.read");
  const queryClient = useQueryClient();
  // T11-43/52: a change of any to-do (hub `staff:inbox`) refreshes the badge at once; the
  // notification-service stream (`inbox`, ADR-0009) even carries the new counts — no refetch
  const poll = useLiveInvalidate("staff:inbox", [["inbox", "summary"]], {
    permission: "inbox.read",
    pollMs: 30_000,
  });
  useLiveTopics([INBOX_TOPIC], (message) => {
    const summary = (message.payload as { summary?: unknown } | undefined)?.summary;
    if (summary && typeof summary === "object" && "open" in summary) {
      queryClient.setQueryData(["inbox", "summary"], summary);
    } else {
      void queryClient.invalidateQueries({ queryKey: ["inbox", "summary"] });
    }
  });
  const summary = useQuery({
    queryKey: ["inbox", "summary"],
    enabled: allowed,
    meta: { silent: true },
    refetchInterval: poll === false ? 30_000 : poll,
    queryFn: ({ signal }) => unwrap(client.GET("/v1/admin/inbox/summary", { signal })),
  });
  useInboxArrivals(session?.user.id ?? "anonymous", summary.data?.open, {
    title: t("header.inbox_new_title"),
    body: t("header.inbox_count", { count: summary.data?.open ?? 0 }),
  });
  if (!allowed) {
    return null;
  }
  const open = summary.data?.open ?? 0;
  const critical = summary.data?.critical ?? 0;
  const label = open > 0 ? t("header.inbox_count", { count: open }) : t("header.inbox");
  return (
    <Link
      to="/inbox"
      aria-label={label}
      title={label}
      data-slot="inbox-bell"
      className="relative inline-flex size-touch items-center justify-center rounded-md hover:bg-accent"
    >
      <Bell className="size-5" aria-hidden />
      {open > 0 ? (
        <span
          aria-hidden
          className={cn(
            "absolute top-1 right-1 min-w-5 rounded-full px-1 text-center text-[0.7rem] leading-5 font-bold text-white",
            critical > 0 ? "bg-[#b91c1c]" : "bg-foreground",
          )}
        >
          {open > 99 ? "99+" : open}
        </span>
      ) : null}
    </Link>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const user = session?.user;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t("header.user_menu")}
        className="inline-flex min-h-touch items-center gap-2 rounded-md px-2 hover:bg-accent"
      >
        <CircleUserRound className="size-6" aria-hidden />
        <span className="hidden max-w-40 truncate text-sm md:inline">{user?.displayName}</span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          <DropdownMenu.Label className="px-3 py-2 text-sm">
            <span className="block font-semibold">{user?.displayName}</span>
            <span className="block text-xs text-muted-foreground">{user?.email}</span>
          </DropdownMenu.Label>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            className="flex min-h-touch cursor-pointer items-center gap-2 rounded px-3 text-sm outline-none data-[highlighted]:bg-accent"
            onSelect={() => void navigate("/profile")}
          >
            <UserRound className="size-4" aria-hidden />
            {t("header.profile")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex min-h-touch cursor-pointer items-center gap-2 rounded px-3 text-sm outline-none data-[highlighted]:bg-accent"
            onSelect={() => void navigate("/help")}
          >
            <LifeBuoy className="size-4" aria-hidden />
            {t("header.help")}
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            className="flex min-h-touch cursor-pointer items-center gap-2 rounded px-3 text-sm outline-none data-[highlighted]:bg-accent"
            onSelect={() => void signOut()}
          >
            <LogOut className="size-4" aria-hidden />
            {t("auth.sign_out")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Top bar: shop name/logo, search (T11-07), language, "● Live" (T11-52), to-do bell, my account. */
export function AppHeader({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const brand = useStoreBrand();
  return (
    <header
      data-slot="app-header"
      className={cn(
        "sticky top-0 z-30 flex items-center gap-2 border-b bg-background px-3",
        compact ? "min-h-[48px]" : "min-h-[56px] sm:px-4",
      )}
    >
      <Link
        to="/"
        className="flex min-h-touch min-w-0 items-center gap-2 font-bold"
        data-slot="brand"
      >
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt="" className="h-7 w-auto max-w-28 object-contain" />
        ) : null}
        <span className="truncate">{brand.name ?? t("app.name")}</span>
      </Link>
      <div className="ml-auto flex items-center gap-1">
        <SearchButton compact={compact} />
        {!compact ? <LanguageSelect /> : null}
        <LiveStatusMenu />
        <InboxBell />
        <UserMenu />
      </div>
    </header>
  );
}
